import { useController, useFormContext } from "react-hook-form";
import { ComponentProps, useMemo, useRef, useState } from "react";
import { SingleSelect } from "@bonliva-crm/ui";
import locale from "@bonliva-crm/locale";
import { WorkedInNorwayOption } from "@bonliva-crm/shared";
import { useUpdateEffect } from "@hooks";
import { isFilterSelected } from "../utils/is-filter-selected";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import { ControllableCollapsibleFavFilter } from "./_base/controllable-collapsible-fav-filter";
import { SingleValueChip } from "./_select/single-value-chip";

export type WorkedInNorwayFilterData = WorkedInNorwayOption;

type Option = { label: string; value: string | null };

export type WorkedInNorwayFilterProps = FilterWithFavouriteProps;

export const WorkedInNorwayFilter = (props: WorkedInNorwayFilterProps) => {
  const options = useMemo(() => {
    return Object.values(WorkedInNorwayOption)
      .filter((x) => x !== "All")
      .toLabelValue((v) => locale.workedInNorwayOption[v]);
  }, []);

  const selectRef = useRef(null);
  const { setValue, resetField } = useFormContext();
  const { field, fieldState } = useController({ name: props.name });
  const [expanded, setExpanded] = useState(fieldState.isDirty);
  const title = props.label ?? locale.WorkedInNorway;
  useUpdateEffect(() => {
    if (expanded) {
      selectRef.current.focus();
    }
  }, [expanded]);

  const clear = () => resetField(props.name);

  const commonProps: ComponentProps<typeof SingleSelect<Option>> = {
    options,
    name: props.name,
    value: field?.value
      ? { label: locale.workedInNorwayOption[field?.value], value: field?.value }
      : null,
    onChange: (selected) => {
      setValue(props.name, selected?.value as WorkedInNorwayOption, {
        shouldDirty: true
      });
    },
    hideSelectedOptions: false,
    controlShouldRenderValue: false,
    placeholder: title,
    isSearchable: false,
    maxMenuHeight: 600,
    showBadge: isFilterSelected(field?.value),
    optionType: "radio",
    isClearable: isFilterSelected(field?.value),
    onClear: () =>
      setValue(props.name, null, {
        shouldDirty: true
      }),
    closeMenuOnSelect: true
  };

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={title}
        isDirty={fieldState.isDirty}
        isClearable={fieldState.isDirty}
        isExpanded={expanded}
        onClear={clear}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <SingleSelect<Option>
          {...commonProps}
          selectRef={selectRef}
          isClearable={false}
          controlShouldRenderValue
          openMenuOnFocus
          menuPortalTarget={document.body}
          showBadge={false}
          placeholder={locale.SelectValue}
          menuPlacement="auto"
          styleProps={{
            menu: { showBorder: true },
            valueContainer: { padding: 0 },
            menuPortal: { zIndex: 9999 }
          }}
          components={{
            SingleValue: (p) => (
              <SingleValueChip {...p} onDelete={() => resetField(props.name)} />
            )
          }}
        />
      </ControllableCollapsibleFavFilter>
    );
  }

  return (
    <SingleSelect<Option>
      {...commonProps}
      styleProps={{
        control: { showBorder: true },
        menu: { showBorder: true }
      }}
    />
  );
};
